/**
 * Every tunable value in the app. Nothing numeric or string-y with meaning
 * lives anywhere else (CODE_RULES.md §9).
 */

/** Self-hosted runtime assets, written by scripts/copy-wasm.mjs. */
export const ASSETS = {
  /** Trailing slash required: web-ifc appends the filename to this path. */
  WASM_DIR: "/wasm/",
  FRAGMENTS_WORKER: "/wasm/fragments-worker.mjs",
} as const;

export const UPLOAD_LIMITS = {
  /**
   * 300 MB. IFC→Fragments conversion runs through WASM memory; beyond roughly
   * this size a browser tab is likely to OOM rather than fail cleanly, so we
   * reject up front with a clear message instead.
   */
  MAX_IFC_BYTES: 300 * 1024 * 1024,
  MAX_PDF_BYTES: 100 * 1024 * 1024,
} as const;

export const FILE_SIGNATURES = {
  /** "ISO-10303-21;" — the STEP physical file header every IFC-SPF starts with. */
  IFC_SPF: "ISO-10303-21",
  /** "%PDF-" */
  PDF: "%PDF-",
  /** Bytes to read for magic-byte sniffing. Generous: some IFCs have a BOM. */
  SNIFF_BYTES: 64,
} as const;

/**
 * The engine renders a small "That Open Company" wordmark bottom-left. The
 * libraries are MIT, so this is courtesy attribution rather than an obligation.
 * Flip to false to hide it.
 */
export const SHOW_ENGINE_LOGO = true;

export const CAMERA = {
  INITIAL_POSITION: [12, 10, 12],
  INITIAL_TARGET: [0, 0, 0],
  /** Vertical span in metres a 2D plan view covers before clipping. */
  PLAN_VIEW_RANGE: 100,
} as const;

export const SCENE = {
  BACKGROUND: "#101418",
  AMBIENT_INTENSITY: 1.2,
  DIRECTIONAL_INTENSITY: 2.4,
  /** Shadow map edge length. 2048 is the quality/VRAM sweet spot for interiors. */
  SHADOW_RESOLUTION: 2048,
  SHADOW_CASCADES: 2,
} as const;

export const HIGHLIGHT = {
  SELECT_COLOR: "#8ab4ff",
  HOVER_COLOR: "#cfe0ff",
} as const;

export const PDF_VIEW = {
  /** Render scale; 1.5 keeps text crisp on standard-DPI displays. */
  BASE_SCALE: 1.5,
  MIN_SCALE: 0.25,
  MAX_SCALE: 5,
  ZOOM_STEP: 0.25,
} as const;

/**
 * IFC classes offered as layer toggles, in the order architects expect.
 * Anything present in the model but not listed here is appended alphabetically,
 * so an unusual model never loses toggles — this is ordering, not a filter.
 */
export const CATEGORY_ORDER: readonly string[] = [
  "IFCWALL",
  "IFCWALLSTANDARDCASE",
  "IFCSLAB",
  "IFCBEAM",
  "IFCCOLUMN",
  "IFCFOOTING",
  "IFCPILE",
  "IFCREINFORCINGBAR",
  "IFCREINFORCINGMESH",
  "IFCDOOR",
  "IFCWINDOW",
  "IFCSTAIR",
  "IFCSTAIRFLIGHT",
  "IFCRAILING",
  "IFCROOF",
  "IFCCOVERING",
  "IFCFURNISHINGELEMENT",
  "IFCSPACE",
] as const;
